import FullCalendar from '@fullcalendar/react';
import { ChevronLeft, ChevronRight, InfoOutlined } from '@mui/icons-material';
import {
  Button,
  ButtonGroup,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { useUISchedulerDateRange } from 'api/queries/ui';
import dayjs from 'dayjs';
import { BookingStatus, DriverStatus, StyledTooltip, colors } from 'remote/global';

import { useTranslation } from 'react-i18next';
import { SchedulerTypeEnum } from 'utils/data/data-types';
import { getMonthViewRange } from 'utils/helpers';

type Props = {
  calendarRef: React.RefObject<FullCalendar>;
  schedulerType: SchedulerTypeEnum;
};

export const schedulerViewOptions = [
  'resourceTimelineDay',
  'resourceTimelineTwoDay',
  'customWeekView',
  'resourceTimelineTwoWeek',
  'customMonthView',
];

function ColorLegend({ scheduleType }: { scheduleType: SchedulerTypeEnum }) {
  const { t } = useTranslation();

  const getLegendColor = (status: BookingStatus | DriverStatus) => {
    switch (scheduleType) {
      case SchedulerTypeEnum.BUS_SCHEDULER:
      case SchedulerTypeEnum.DRIVER_SCHEDULER:
        switch (status) {
          case BookingStatus.NO_DRIVER:
            return colors.busyRed;
          case BookingStatus.DRIVER_ASSIGNED:
            return colors.restYellow;
          case BookingStatus.CONFIRMED:
            return '#9DD5F6';
          case BookingStatus.STARTED:
            return colors.uiBlue600;
          case BookingStatus.FINISHED:
            return colors.activeGreen;
          case BookingStatus.PAUSED:
            return colors.gray3;
          default:
            return colors.busyRed;
        }
      case SchedulerTypeEnum.DRIVER_WORK_SCHEDULER:
        switch (status) {
          case DriverStatus.ACTIVE:
            return colors.activeGreen;
          case DriverStatus.AVAILABLE:
            return colors.uiBlue500;
          case DriverStatus.VACATION:
            return colors.gray1;
          case DriverStatus.SICKNESS:
            return colors.primaryBlue3;
          case DriverStatus.TIME_OFF:
            return colors.restYellow;
          default:
            return colors.busyRed;
        }
      default:
        return colors.busyRed;
    }
  };

  const getLegendOptions = () => {
    switch (scheduleType) {
      case SchedulerTypeEnum.BUS_SCHEDULER:
      case SchedulerTypeEnum.DRIVER_SCHEDULER:
        return BookingStatus;
      case SchedulerTypeEnum.DRIVER_WORK_SCHEDULER:
        return DriverStatus;
      default:
        return BookingStatus;
    }
  };

  return (
    <Box>
      {Object.values(getLegendOptions()).map((status) => {
        let statusString = t(`EventStatus.${status}`);
        if (scheduleType === SchedulerTypeEnum.DRIVER_WORK_SCHEDULER)
          statusString = t(`DriverStatus.${status}`);
        return (
          <Box
            key={status}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '2px',
                backgroundColor: getLegendColor(status),
              }}
            />
            <Typography
              sx={{
                color: colors.headerBlack,
                fontSize: '14px',
              }}
            >
              {statusString}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function FullCalendarToolbar({ calendarRef, schedulerType }: Props) {
  const { setSchedulerDateRange } = useUISchedulerDateRange();
  const { t, i18n } = useTranslation();
  dayjs.locale(i18n.language);

  const calendarDate =
    calendarRef.current != null ? calendarRef.current.getApi().getDate() : new Date();
  const currentViewType = calendarRef?.current?.getApi().view.type;
  const calendarStartDate = calendarRef?.current?.getApi().view.currentStart;
  const calendarEndDate = calendarRef?.current?.getApi().view.currentEnd;

  const handlePrev = () => {
    if (!calendarRef.current) return;

    switch (currentViewType) {
      case 'pastAndFutureView': {
        const dayDiff = Math.abs(dayjs(calendarStartDate).diff(calendarEndDate, 'day'));
        const start = dayjs(calendarStartDate)
          .subtract(dayDiff + 1, 'day')
          .toDate();
        const end = dayjs(calendarStartDate).toDate();
        calendarRef.current.getApi().setOption('visibleRange', { start, end });
        setSchedulerDateRange([dayjs(start), dayjs(end).subtract(1, 'day')]);
        break;
      }
      case 'customMonthView':
        calendarRef.current
          .getApi()
          .setOption(
            'visibleRange',
            getMonthViewRange(dayjs(calendarStartDate).subtract(1, 'month').toDate())
          );
        break;
      default:
        setSchedulerDateRange([null, null]);
        calendarRef.current.getApi().prev();
    }
  };

  const handleNext = () => {
    if (!calendarRef.current) return;

    if (currentViewType !== 'pastAndFutureView') {
      setSchedulerDateRange([null, null]);
    }

    switch (currentViewType) {
      case 'pastAndFutureView': {
        const start = dayjs(calendarStartDate).toDate();
        const end = dayjs(calendarEndDate).toDate();
        const dayDiff = Math.abs(dayjs(calendarStartDate).diff(calendarEndDate, 'day'));
        const finalStart = dayjs(end).add(1, 'day').toDate();
        const finalEnd = dayjs(start)
          .add(dayDiff * 2 + 2, 'day')
          .toDate();
        calendarRef.current.getApi().setOption('visibleRange', {
          start: finalStart,
          end: finalEnd,
        });
        setSchedulerDateRange([dayjs(finalStart), dayjs(finalEnd).subtract(1, 'day')]);
        break;
      }
      case 'customMonthView':
        calendarRef.current
          .getApi()
          .setOption(
            'visibleRange',
            getMonthViewRange(dayjs(calendarStartDate).add(1, 'month').toDate())
          );
        break;
      default:
        calendarRef.current.getApi().next();
    }
  };

  const handleSetToday = () => {
    if (!calendarRef.current) return;

    if (currentViewType !== 'pastAndFutureView') {
      setSchedulerDateRange([null, null]);
    }

    switch (currentViewType) {
      case 'pastAndFutureView': {
        const dayDiff = Math.abs(dayjs(calendarStartDate).diff(calendarEndDate, 'day'));
        const start = dayjs().subtract(Math.ceil(dayDiff / 2), 'day');
        const end = dayjs().add(Math.floor(dayDiff / 2), 'day');

        calendarRef.current
          .getApi()
          .setOption('visibleRange', { start: start.toDate(), end: end.toDate() });
        setSchedulerDateRange([start, end]);
        break;
      }
      case 'customMonthView':
        calendarRef.current.getApi().setOption('visibleRange', getMonthViewRange(new Date()));
        break;
      default:
        calendarRef.current.getApi().today();
    }
  };

  const getIsTodayDisabled = () => {
    if (!calendarRef.current) return true;

    switch (currentViewType) {
      case 'customMonthView':
        return dayjs(calendarStartDate).isSame(dayjs(), 'month');
      case 'customWeekView':
        return dayjs(calendarStartDate).isSame(dayjs(), 'week');
      case 'resourceTimelineTwoWeek':
        return (
          dayjs(calendarStartDate).isSame(dayjs(), 'week') ||
          dayjs(calendarEndDate).subtract(1, 'day').isSame(dayjs(), 'week')
        );
      case 'resourceTimelineTwoDay':
      case 'pastAndFutureView': {
        // Check if today is equal or between the range of dates
        const subtractedEnd = dayjs(calendarEndDate).subtract(1, 'day');
        const isSameDay =
          dayjs(calendarStartDate).isSame(dayjs(), 'day') ||
          dayjs(subtractedEnd).isSame(dayjs(), 'day');
        const isBetween =
          isSameDay || (dayjs().isAfter(calendarStartDate) && dayjs().isBefore(subtractedEnd));
        return isBetween;
      }
      default:
        return dayjs(calendarDate).isSame(dayjs(), 'day');
    }
  };

  const handleSetCalendarView = (view: string) => {
    if (!calendarRef.current) return;

    calendarRef.current.getApi().changeView(view);

    if (view !== 'pastAndFutureView') {
      setSchedulerDateRange([null, null]);
    }

    switch (view) {
      case 'customMonthView':
        calendarRef.current.getApi().setOption('visibleRange', getMonthViewRange(calendarDate));
        break;
      default:
    }

    // Remember this choice in sessionStorage
    if (schedulerViewOptions.includes(view)) {
      sessionStorage.setItem(schedulerType, view);
    }
  };

  const getCurrentDateByView = (viewType: string) => {
    // End date is set at last day + 1
    const endDate = calendarEndDate;
    endDate?.setDate(endDate.getDate() - 1);

    let str = dayjs(calendarStartDate).format('LL');

    if (viewType == null) return str;

    switch (viewType) {
      case 'resourceTimelineTwoDay':
        str = `${dayjs(calendarStartDate).format('D')} - ${dayjs(endDate).format('ll')}`;
        break;
      case 'customWeekView':
        str = `${dayjs(calendarStartDate).startOf('week').format('ll')} - ${dayjs(endDate).format(
          'll'
        )}`;
        break;
      case 'resourceTimelineTwoWeek':
        str = `${dayjs(calendarStartDate).startOf('week').format('ll')} - ${dayjs(endDate).format(
          'll'
        )}`;
        break;
      case 'pastAndFutureView':
        str = `${dayjs(calendarStartDate).format('ll')} - ${dayjs(endDate).format('ll')}`;
        break;
      case 'customMonthView':
        str = `${dayjs(calendarStartDate).format('MMMM YYYY')}`;
        break;
      default:
        str = dayjs(calendarStartDate).format('LL');
    }

    return str;
  };

  return (
    <Grid
      container
      component='header'
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1rem',
        rowGap: 2,
      }}
    >
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <ButtonGroup
          size='small'
          variant='outlined'
          sx={{
            '.MuiButtonGroup-grouped:not(:last-of-type)': {
              border: '1px solid #CBD5E1',
            },
            '.MuiButtonGroup-grouped:not(:first-of-type)': {
              border: '1px solid #CBD5E1',
            },
          }}
        >
          <Button type='button' onClick={handlePrev}>
            <ChevronLeft />
          </Button>
          <Button type='button' onClick={handleNext}>
            <ChevronRight />
          </Button>
        </ButtonGroup>
        <Button
          type='button'
          size='small'
          variant='outlined'
          onClick={handleSetToday}
          disabled={getIsTodayDisabled()}
        >
          {t('SchedulerView.attributes.today')}
        </Button>
        <Typography
          sx={{
            color: colors.headerBlack,
          }}
        >
          {getCurrentDateByView(currentViewType as string)}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <StyledTooltip title={<ColorLegend scheduleType={schedulerType} />}>
          <InfoOutlined sx={{ color: colors.uiBlue700 }} />
        </StyledTooltip>
        <ToggleButtonGroup size='small' value={currentViewType}>
          {schedulerViewOptions.map((view) => {
            return (
              <ToggleButton
                sx={{
                  bgcolor: colors.gray6,
                  '&.Mui-selected': {
                    bgcolor: colors.gray13,
                  },
                }}
                value={view}
                key={view}
                onClick={() => handleSetCalendarView(view)}
              >
                {t(`SchedulerView.views.${view}`)}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Grid>
    </Grid>
  );
}

export default FullCalendarToolbar;
